import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const EMPTY_STATS = {
    total_saved: 0,
    mutual_count: 0,
    mutual_users: [],
    other_count: 0,
    other_users: [],
    is_saved_by_me: false,
};

/**
 * Údaje pro social proof („koho z mé sítě znám, kdo si to uložil“).
 *
 * - data přebírají z payloadu stránky, ať nejsou dvě kila navíc,
 * - když chybí, dojedou tichým dotazem přes /api/social-proof,
 * - po realtime události se přepočítají jen tehdy, když se počet změnil,
 * - chyba se nepropadá dál – komponenta se prostě neukáže.
 */
export const useSocialProof = ({
    type,
    targetId,
    initialStats = null,
    subscribe = false,
}) => {
    const [stats, setStats] = useState(initialStats || EMPTY_STATS);
    const [loading, setLoading] = useState(!initialStats);
    const [error, setError] = useState(false);

    // Výsledek posledního načtení, aby se realtime updater nestahoval zbytečně
    const latest = useRef(stats);
    latest.current = stats;

    const refresh = useCallback(
        async ({ silent = true } = {}) => {
            if (!targetId) return;

            if (!silent) setLoading(true);

            try {
                const response = await axiosInstance.get(
                    `/api/social-proof/${type}/${targetId}`,
                );
                setStats(response.data.social_proof || EMPTY_STATS);
                setError(false);
            } catch (e) {
                // Tichá chyba – rozpadlá se jen informace, ne celá stránka
                setError(true);
            } finally {
                setLoading(false);
            }
        },
        [type, targetId],
    );

    useEffect(() => {
        if (initialStats) {
            setStats(initialStats);
            setLoading(false);

            return;
        }

        refresh();
    }, [initialStats, refresh]);

    // Realtime: změnu známe jen jako celkový počet, zbytek se dojede dotazem.
    useEffect(() => {
        if (!subscribe || !targetId || !window.Echo) return;

        const channelName = `social-proof.${type}.${targetId}`;
        const channel = window.Echo.channel(channelName);

        channel.listen(".favourite.stats.updated", (event) => {
            if (event?.total_saved !== latest.current.total_saved) {
                refresh();
            }
        });

        return () => {
            window.Echo?.leaveChannel(channelName);
        };
    }, [subscribe, type, targetId, refresh]);

    /**
     * Propsání čerstvých údajů z odpovědi po uložení – netřeba další dotaz.
     */
    const merge = useCallback((next) => {
        if (next) {
            setStats(next);
            setError(false);
        }
    }, []);

    /**
     * Okamžitá reakce na vlastní uložení. Čísla se nemění – počítáme
     * ostatní – obrací se jen příznak, ať je tlačítko hned konzistentní.
     */
    const applyLocalToggle = useCallback((status) => {
        setStats((prev) => ({
            ...prev,
            is_saved_by_me: status === "added",
        }));
    }, []);

    return {
        stats,
        loading,
        error,
        refresh,
        merge,
        applyLocalToggle,
    };
};

export default useSocialProof;
